<template>
  <div>
    <base-header class="pb-6 pb-1 pt-4 pt-md-8 bg-gradient-success">
      <!-- Header content -->
    </base-header>

    <div class="container-fluid mt--7">
      <div class="row">
        <div class="col">
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Control de Stock - Menú Diario</h3>
                </div>
                <div class="col text-right">
                  <button @click="loadStock" :disabled="loading" class="btn btn-sm btn-primary">
                    <i class="fas fa-sync-alt"></i> Actualizar
                  </button>
                </div>
              </div>
            </div>

            <!-- Filtros -->
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <base-input label="Fecha">
                    <input
                      type="date"
                      v-model="filters.date"
                      @change="loadStock"
                      class="form-control"
                    />
                  </base-input>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Cargando...</span>
                </div>
              </div>

              <!-- Sin catering seleccionado -->
              <div v-else-if="!filters.catering_id" class="text-center py-5">
                <i class="fas fa-store fa-3x text-muted mb-3"></i>
                <p class="text-muted">Selecciona un catering para ver el stock</p>
              </div>

              <!-- Sin pedido aprobado -->
              <div v-else-if="stock.length === 0" class="text-center py-5">
                <i class="fas fa-box-open fa-3x text-muted mb-3"></i>
                <p class="text-muted">No hay pedido aprobado para esta fecha</p>
              </div>

              <!-- Tabla de stock -->
              <div v-else>
                <div class="table-responsive">
                  <table class="table align-items-center table-flush">
                    <thead class="thead-light">
                      <tr>
                        <th>Plato</th>
                        <th class="text-center">Stock Inicial</th>
                        <th class="text-center">Ventas Delivery</th>
                        <th class="text-center">Ventas Físicas</th>
                        <th class="text-center">Stock Actual</th>
                        <th class="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in stock" :key="item.menu_meal_id">
                        <td>
                          <strong>{{ item.meal_name }}</strong><br />
                          <small class="text-muted">{{ item.meal_type_name }}</small>
                        </td>
                        <td class="text-center">
                          <span class="badge badge-info">{{ item.stock_inicial }}</span>
                        </td>
                        <td class="text-center">
                          <span class="text-muted">{{ item.ventas_delivery }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge badge-warning">{{ item.ventas_fisicas }}</span>
                        </td>
                        <td class="text-center">
                          <span
                            class="badge"
                            :class="{
                              'badge-danger': item.sin_stock,
                              'badge-warning': !item.sin_stock && item.stock_actual <= 5,
                              'badge-success': !item.sin_stock && item.stock_actual > 5
                            }"
                          >
                            {{ item.stock_actual }}
                          </span>
                          <span v-if="item.sin_stock" class="badge badge-danger badge-sm ml-1">
                            SIN STOCK
                          </span>
                        </td>
                        <td class="text-center">
                          <div class="d-flex align-items-center justify-content-center">
                            <button
                              @click="confirmRegisterSale(item, 1)"
                              :disabled="item.sin_stock || item.saving"
                              class="btn btn-sm btn-success"
                            >
                              <i class="fas fa-plus"></i> +1 Vendido
                            </button>
                            
                            <input
                              type="number"
                              v-model.number="item.customQuantity"
                              min="1"
                              :max="item.stock_actual"
                              :disabled="item.sin_stock"
                              class="form-control form-control-sm ml-2"
                              style="max-width: 80px;"
                              placeholder="Cant."
                            />
                            
                            <button
                              v-if="item.customQuantity && item.customQuantity > 0"
                              @click="confirmRegisterSale(item, item.customQuantity)"
                              :disabled="item.sin_stock || item.customQuantity > item.stock_actual || item.saving"
                              class="btn btn-sm btn-success ml-2"
                            >
                              <i class="fas fa-check"></i> Vender
                            </button>
                            
                            <button
                              @click="showHistory(item)"
                              class="btn btn-sm btn-info ml-2"
                            >
                              <i class="fas fa-history"></i> Historial
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Resumen del Día -->
                <div class="row mt-4">
                  <div class="col-md-3">
                    <div class="card card-stats">
                      <div class="card-body">
                        <div class="row">
                          <div class="col">
                            <h5 class="card-title text-uppercase text-muted mb-0">Platos</h5>
                            <span class="h2 font-weight-bold mb-0">{{ stock.length }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card card-stats">
                      <div class="card-body">
                        <div class="row">
                          <div class="col">
                            <h5 class="card-title text-uppercase text-muted mb-0">Stock Inicial</h5>
                            <span class="h2 font-weight-bold mb-0">{{ getTotalStockInicial() }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card card-stats">
                      <div class="card-body">
                        <div class="row">
                          <div class="col">
                            <h5 class="card-title text-uppercase text-muted mb-0">Ventas Físicas</h5>
                            <span class="h2 font-weight-bold mb-0 text-warning">{{ getTotalVentasFisicas() }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-3">
                    <div class="card card-stats">
                      <div class="card-body">
                        <div class="row">
                          <div class="col">
                            <h5 class="card-title text-uppercase text-muted mb-0">Stock Actual</h5>
                            <span
                              class="h2 font-weight-bold mb-0"
                              :class="{
                                'text-danger': getTotalStockActual() <= 0,
                                'text-success': getTotalStockActual() > 0
                              }"
                            >
                              {{ getTotalStockActual() }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de historial -->
    <custom-modal 
      :is-modal="historyDialog" 
      @closeModal="historyDialog = false"
      :closeOuth="true"
      width="xxl"
      title="Historial de Ventas"
    >
      <div v-if="loadingHistory" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Cargando...</span>
        </div>
      </div>

      <div v-else-if="!mealHistory || mealHistory.length === 0" class="text-center py-4">
        <i class="fas fa-history fa-3x text-muted mb-3"></i>
        <p class="text-muted">No hay historial de ventas para este plato</p>
      </div>

      <div v-else>
        <h5 class="mb-3">
          <i class="fas fa-utensils text-info"></i>
          {{ selectedMeal && selectedMeal.meal_name }}
        </h5>
        <!-- Resumen -->
        <div class="row mb-3">
          <div class="col-md-4">
            <div class="card card-stats mb-3">
              <div class="card-body">
                <h5 class="card-title text-uppercase text-muted mb-0">Ventas Delivery</h5>
                <span class="h2 font-weight-bold mb-0 text-info">
                  {{ historySummary ? historySummary.total_delivery : 0 }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card card-stats mb-3">
              <div class="card-body">
                <h5 class="card-title text-uppercase text-muted mb-0">Ventas Tienda</h5>
                <span class="h2 font-weight-bold mb-0 text-warning">
                  {{ historySummary ? historySummary.total_tienda : 0 }}
                </span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card card-stats mb-3">
              <div class="card-body">
                <h5 class="card-title text-uppercase text-muted mb-0">Total General</h5>
                <span class="h2 font-weight-bold mb-0 text-success">
                  {{ historySummary ? historySummary.total_general : 0 }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabla de historial -->
        <h4 class="mb-3">📋 Detalle de Movimientos ({{ mealHistory.length }})</h4>
        <div class="table-responsive">
          <table class="table align-items-center table-sm">
            <thead class="thead-light">
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th class="text-center">Cantidad</th>
                <th class="text-center">Tipo</th>
                <th class="text-center">Origen</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in mealHistory" :key="index">
                <td>{{ formatDate(item.date) }}</td>
                <td><strong>{{ formatTime(item.time) }}</strong></td>
                <td class="text-center">
                  <span class="badge badge-primary">{{ item.quantity }}</span>
                </td>
                <td class="text-center">
                  <span
                    class="badge"
                    :class="{
                      'badge-info': item.type === 'delivery',
                      'badge-warning': item.type === 'tienda'
                    }"
                  >
                    <i :class="item.type === 'delivery' ? 'fas fa-motorcycle' : 'fas fa-store'"></i>
                    {{ item.type_label }}
                  </span>
                </td>
                <td class="text-center">
                  <span
                    class="badge badge-outline-secondary"
                    :class="{
                      'badge-outline-primary': item.origin === 'manzana_verde',
                      'badge-outline-success': item.origin === 'catering_app'
                    }"
                  >
                    <i :class="item.origin === 'manzana_verde' || item.origin === 'backoffice' ? 'fas fa-desktop' : 'fas fa-mobile-alt'"></i>
                    {{ item.origin_label }}
                  </span>
                </td>
                <td>
                  <i class="fas fa-user"></i> {{ item.user_name || 'Sistema' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </custom-modal>
  </div>
</template>

<script>
  import moment from 'moment';
  import backofficeApi from '@/gateways/backoffice-api';
  import { toV3 } from '@/utils/apiHelpers';
  import CustomModal from '@/components/CustomModal';
  import swal from 'sweetalert2';

export default {
  name: 'ControlStockTienda',
  components: {
    CustomModal,
  },
  data() {
    return {
      loading: false,
      stock: [],
      selectedMeal: null,
      historyDialog: false,
      loadingHistory: false,
      mealHistory: [],
      historySummary: null,
      filters: {
        date: new Date().toISOString().substr(0, 10),
        catering_id: null,
      },
    };
  },
  created() {
    this.initializeCatering();
  },
  methods: {
    initializeCatering() {
      try {
        // Obtener catering_id del localStorage
        const userData = JSON.parse(localStorage.getItem('userData'));
        const cateringId = userData ? userData.catering_id : undefined;
        
        if (cateringId) {
          this.filters.catering_id = cateringId;
          this.loadStock();
        } else {
          this.showError('No se pudo obtener el catering del usuario');
        }
      } catch (error) {
        console.error('Error obteniendo datos del usuario:', error);
        this.showError('Error al obtener el catering del usuario');
      }
    },
    async loadStock() {
      if (!this.filters.catering_id) {
        return;
      }

      this.loading = true;
      try {
        const response = await backofficeApi.get(toV3(`physical-orders/stock-control/${this.filters.date}/${this.filters.catering_id}`), {
          params: {
            date: this.filters.date,
            catering_id: this.filters.catering_id,
          },
        });

        if (response.data && response.data.success) {
          this.stock = (response.data.detail.stock || []).map(item => ({
            ...item,
            customQuantity: null,
            saving: false,
          }));
        }
      } catch (error) {
        console.error('Error loading stock:', error);
        this.showError('Error al cargar el stock');
      } finally {
        this.loading = false;
      }
    },
    confirmRegisterSale(item, quantity) {
      swal({
        title: '¿Registrar venta?',
        html: `
          <p><strong>${item.meal_name}</strong></p>
          <p>Cantidad: <strong>${quantity}</strong> plato(s)</p>
          <p>Stock actual: <strong>${item.stock_actual}</strong></p>
        `,
        type: 'question',
        showCancelButton: true,
        confirmButtonColor: '#2dce89',
        cancelButtonColor: '#f5365c',
        confirmButtonText: '<i class="fas fa-check"></i> Sí, registrar',
        cancelButtonText: '<i class="fas fa-times"></i> Cancelar',
        reverseButtons: true,
        buttonsStyling: true,
      }).then((result) => {
        if (result.value) {
          this.registerSale(item, quantity);
        }
      });
    },
    async registerSale(item, quantity) {
      if (!quantity || quantity <= 0) {
        this.showError('La cantidad debe ser mayor a 0');
        return;
      }

      if (quantity > item.stock_actual) {
        this.showError('No hay suficiente stock disponible');
        return;
      }

      item.saving = true;
      try {
        const response = await backofficeApi.post(toV3('physical-orders/register-sale'), {
          date: this.filters.date,
          catering_id: this.filters.catering_id,
          menu_meal_id: item.menu_meal_id,
          quantity: quantity,
        });

        if (response.data && response.data.success) {
          // Actualizar el item con los nuevos valores
          const stockInfo = response.data.detail.stock_actual;
          if (stockInfo) {
            item.ventas_fisicas = stockInfo.ventas_fisicas;
            item.stock_actual = stockInfo.stock_actual;
            item.sin_stock = stockInfo.sin_stock;
          }
          item.customQuantity = null;
          
          // Recargar el stock completo
          await this.loadStock();
          this.showSuccess(`Venta de ${quantity} plato(s) registrada correctamente`);
        }
      } catch (error) {
        console.error('Error registering sale:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Error al registrar la venta';
        this.showError(errorMessage);
      } finally {
        item.saving = false;
      }
    },
    async showHistory(item) {
      this.selectedMeal = item;
      this.historyDialog = true;
      this.loadingHistory = true;
      this.mealHistory = [];
      this.historySummary = null;

      try {
        const response = await backofficeApi.get(toV3('physical-orders/meal-history'), {
          params: {
            date: this.filters.date,
            catering_id: this.filters.catering_id,
            menu_meal_id: item.menu_meal_id,
          },
        });

        if (response.data && response.data.success) {
          this.mealHistory = response.data.detail.history || [];
          this.historySummary = response.data.detail.summary || null;
        }
      } catch (error) {
        console.error('Error loading history:', error);
        this.showError('Error al cargar el historial');
      } finally {
        this.loadingHistory = false;
      }
    },
    getTotalStockInicial() {
      return this.stock.reduce((sum, item) => sum + item.stock_inicial, 0);
    },
    getTotalVentasFisicas() {
      return this.stock.reduce((sum, item) => sum + item.ventas_fisicas, 0);
    },
    getTotalStockActual() {
      return this.stock.reduce((sum, item) => sum + item.stock_actual, 0);
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    formatTime(time) {
      if (!time) return '-';
      const timeStr = typeof time === 'string' ? time : time.toString();
      return timeStr.substring(0, 5);
    },
    showSuccess(message) {
      this.$notify({
        type: 'success',
        message: message,
        icon: 'fas fa-check',
      });
    },
    showError(message) {
      this.$notify({
        type: 'danger',
        message: message,
        icon: 'fas fa-exclamation-triangle',
      });
    },
  },
};
</script>

<style scoped>
.badge-outline-secondary {
  border: 1px solid #dee2e6;
}
.badge-outline-primary {
  border: 1px solid #5e72e4;
  color: #5e72e4;
}
.badge-outline-success {
  border: 1px solid #2dce89;
  color: #2dce89;
}
</style>

